import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointAssetsLiabilities";

import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import RadioButton from "app/components/common/buttons/RadioButton";
import AddMore from "app/components/common/buttons/AddMore";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { JointAssetsLiabilitiesPartOneFormData } from "app/utils/types";
import { useAddAssetsLiabilities } from "app/hooks/assetsLiabilities/useAddAssetsLiabilities";
import { useNavigate } from "react-router-dom";
import { ASSST_LIABILITY } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import { useDeleteItem } from "app/hooks/useDeleteItem";
import DynamicRealEstate from "app/components/Individual/applicant/assetsLiabilities/DynamicRealEstate";
import DynamicSuperannuation from "app/components/Individual/applicant/assetsLiabilities/DynamicSuperannuation";

const PartOne: React.FC = () => {
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync: submitAssetsLiabilities, isPending } =
    useAddAssetsLiabilities();
  const { formOne, ...others } = useSelector(
    (state: RootState) => state?.jointAssetsLiabilities?.stepsData
  ) || {
    formOne: { realState: [], superannuation: [] },
  };
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    register,
    setValue,
    setError,
    clearErrors,
  } = useForm<JointAssetsLiabilitiesPartOneFormData>({
    defaultValues: {
      yourDetails: {
        realeStateType: "no",
        superannuationType: "no",
        realState: [{ detail: "", value: "" }],
        superannuation: [{ nameOfFund: "", value: "" }],
      },
      coBorrowerDetails: {
        realeStateType: "no",
        superannuationType: "no",
        realState: [{ detail: "", value: "" }],
        superannuation: [{ nameOfFund: "", value: "" }],
      },
    },
  });

  const realestateValue = watch("yourDetails.realeStateType");
  const corealestate = watch("coBorrowerDetails.realeStateType");
  const superannuationValue = watch("yourDetails.superannuationType");
  const cosuperannuationValue = watch("coBorrowerDetails.superannuationType");

  const onSubmit = async (data: JointAssetsLiabilitiesPartOneFormData) => {
    const { yourDetails, coBorrowerDetails } = data;
    if (!savedIds?.applicantID && !savedIds?.secondApplicantID) {
      toast.error("Applicant id is required");
      return;
    }

    const yourDetailsPayload = {
      realState:
        yourDetails.realeStateType === "yes"
          ? yourDetails?.realState?.map((item: any) => ({
              id: item.dbId,
              detail: item.detail,
              value: Number(item.value),
            }))
          : null,
      superannuation:
        yourDetails.superannuationType === "yes"
          ? yourDetails.superannuation?.map((item: any) => ({
              id: item.dbId,
              nameOfFund: item.nameOfFund,
              value: Number(item.value),
            }))
          : null,
    };

    const coBorrowerDetailsPayload = {
      realState:
        coBorrowerDetails.realeStateType === "yes"
          ? coBorrowerDetails?.realState?.map((item: any) => ({
              id: item.dbId,
              detail: item.detail,
              value: Number(item.value),
            }))
          : null,
      superannuation:
        coBorrowerDetails.superannuationType === "yes"
          ? coBorrowerDetails?.superannuation?.map((item: any) => ({
              id: item.dbId,
              nameOfFund: item.nameOfFund,
              value: Number(item.value),
            }))
          : null,
    };

    const request1 = {
      url: `${ASSST_LIABILITY}/${savedIds?.applicantID}/first`,
      data: yourDetailsPayload,
    };
    const request2 = {
      url: `${ASSST_LIABILITY}/${savedIds?.secondApplicantID}/first`,
      data: coBorrowerDetailsPayload,
    };

    try {
      const [response1, response2] = await Promise.all([
        submitAssetsLiabilities(request1),
        submitAssetsLiabilities(request2),
      ]);

      if (response1 && response2) {
        const yourRealStateMapped = response1?.realStates?.map((item: any) => ({
          dbId: item.id,
          detail: item.detail,
          value: item.value?.toString(),
        }));
        const yourSuperannuationMapped = response1?.superannuations?.map(
          (item: any) => ({
            dbId: item.id,
            nameOfFund: item.nameOfFund,
            value: item.value?.toString(),
          })
        );

        const coRealStateMapped = response2?.realStates?.map((item: any) => ({
          dbId: item.id,
          detail: item.detail,
          value: item.value?.toString(),
        }));
        const coSuperannuationMapped = response2?.superannuations?.map(
          (item: any) => ({
            dbId: item.id,
            nameOfFund: item.nameOfFund,
            value: item.value?.toString(),
          })
        );

        const savedData = {
          yourDetails: {
            ...formOne?.yourDetails,
            realState:
              yourDetails.realeStateType === "no" ? [] : yourRealStateMapped,
            superannuation:
              yourDetails.superannuationType === "no"
                ? []
                : yourSuperannuationMapped,
            realeStateType: yourDetails.realeStateType,
            superannuationType: yourDetails.superannuationType,
          },
          coBorrowerDetails: {
            ...formOne?.coBorrowerDetails,
            realState:
              coBorrowerDetails.realeStateType === "no"
                ? []
                : coRealStateMapped,
            superannuation:
              coBorrowerDetails.superannuationType === "no"
                ? []
                : coSuperannuationMapped,
            realeStateType: coBorrowerDetails.realeStateType,
            superannuationType: coBorrowerDetails.superannuationType,
          },
        };
        dispatch(
          addStepsData({
            ...others,
            formOne: savedData,
          })
        );
        dispatch(nextStep());
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "yourDetails.realState",
  });
  const {
    fields: superannuationFields,
    append: superannuationAppend,
    remove: superannuationRemove,
  } = useFieldArray({
    control,
    name: "yourDetails.superannuation",
  });

  const {
    fields: cofields,
    append: coappend,
    remove: coremove,
  } = useFieldArray({
    control,
    name: "coBorrowerDetails.realState",
  });

  const {
    fields: cosuperannuationFields,
    append: cosuperannuationAppend,
    remove: cosuperannuationRemove,
  } = useFieldArray({
    control,
    name: "coBorrowerDetails.superannuation",
  });

  useEffect(() => {
    if (formOne && Object.keys(formOne).length > 0) {
      reset({
        yourDetails: {
          ...formOne.yourDetails,
          realState:
            formOne.yourDetails?.realeStateType === "no"
              ? [{ detail: "", value: "" }]
              : formOne.yourDetails.realState || [{ detail: "", value: "" }],
          superannuation:
            formOne.yourDetails?.superannuationType === "no"
              ? [{ nameOfFund: "", value: "" }]
              : formOne.yourDetails.superannuation || [
                  { nameOfFund: "", value: "" },
                ],
        },
        coBorrowerDetails: {
          ...formOne.coBorrowerDetails,
          realState:
            formOne.coBorrowerDetails?.realeStateType === "no"
              ? [{ detail: "", value: "" }]
              : formOne.coBorrowerDetails.realState || [
                  { detail: "", value: "" },
                ],
          superannuation:
            formOne.coBorrowerDetails?.superannuationType === "no"
              ? [{ nameOfFund: "", value: "" }]
              : formOne.coBorrowerDetails.superannuation || [
                  { nameOfFund: "", value: "" },
                ],
        },
      });
    }
  }, [formOne, reset]);

  const handlePrevious = () => {
    navigate(`/joint-income-details`);
  };

  return (
    <>
      <ProgressBar progress={0} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0 mb-3">
        <MainTitle title="Real Estate/Superannuation" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-8 flex-col md:flex-col xl:flex-row sm:gap-4">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any real estate?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="yourDetails.realeStateType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="realestate_yes"
                          name="realestate"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="realestate_no"
                          name="realestate"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.yourDetails?.realeStateType && (
                  <ErrorsMessage
                    title={errors.yourDetails.realeStateType.message}
                    className="!text-left"
                  />
                )}
              </div>

              {realestateValue === "yes" && (
                <>
                  <DynamicRealEstate
                    control={control}
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    setError={setError}
                    clearErrors={clearErrors}
                    fields={fields}
                    deleteItem={deleteItem}
                    realState="yourDetails.realState"
                    applicantID={savedIds?.applicantID as string}
                    remove={remove}
                    deleteIsPending={deleteIsPending}
                  />

                  {fields.length < 3 && (
                    <div className="flex items-center justify-end mt-6 mb-4 sm:mb-2">
                      <AddMore
                        onClick={() => append({ detail: "", value: "" })}
                      />
                    </div>
                  )}
                </>
              )}

              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any superannuation?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="yourDetails.superannuationType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="superannuation_yes"
                          name="yourDetails.superannuationType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="superannuation_no"
                          name="yourDetails.superannuationType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.yourDetails?.superannuationType && (
                  <ErrorsMessage
                    title={errors.yourDetails.superannuationType.message}
                    className="!text-left"
                  />
                )}
              </div>

              {superannuationValue === "yes" && (
                <>
                  <DynamicSuperannuation
                    control={control}
                    errors={errors}
                    fields={superannuationFields}
                    deleteItem={deleteItem}
                    superannuation="yourDetails.superannuation"
                    applicantID={savedIds?.applicantID as string}
                    remove={superannuationRemove}
                    deleteIsPending={deleteIsPending}
                  />
                  {superannuationFields.length < 3 && (
                    <div className="flex items-center justify-end mt-6">
                      <AddMore
                        onClick={() =>
                          superannuationAppend({
                            nameOfFund: "",
                            value: "",
                          })
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />

              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any real estate?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="coBorrowerDetails.realeStateType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="corealestate_yes"
                          name="coBorrowerDetails.realeStateType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="corealestate_no"
                          name="coBorrowerDetails.realeStateType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.coBorrowerDetails?.realeStateType && (
                  <ErrorsMessage
                    title={errors.coBorrowerDetails.realeStateType.message}
                    className="!text-left"
                  />
                )}
              </div>

              {corealestate === "yes" && (
                <>
                  <DynamicRealEstate
                    control={control}
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    setError={setError}
                    clearErrors={clearErrors}
                    fields={cofields}
                    deleteItem={deleteItem}
                    realState="coBorrowerDetails.realState"
                    applicantID={savedIds?.secondApplicantID as string}
                    remove={coremove}
                    deleteIsPending={deleteIsPending}
                  />

                  {cofields.length < 3 && (
                    <div className="flex items-center justify-end mt-6 mb-4 sm:mb-2">
                      <AddMore
                        onClick={() => coappend({ detail: "", value: "" })}
                      />
                    </div>
                  )}
                </>
              )}

              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any superannuation?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="coBorrowerDetails.superannuationType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="cosuperannuation_yes"
                          name="coBorrowerDetails.superannuationType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="cosuperannuation_no"
                          name="coBorrowerDetails.superannuationType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.coBorrowerDetails?.superannuationType && (
                  <ErrorsMessage
                    title={errors.coBorrowerDetails.superannuationType.message}
                    className="!text-left"
                  />
                )}
              </div>

              {cosuperannuationValue === "yes" && (
                <>
                  <DynamicSuperannuation
                    control={control}
                    errors={errors}
                    fields={cosuperannuationFields}
                    deleteItem={deleteItem}
                    superannuation="coBorrowerDetails.superannuation"
                    applicantID={savedIds?.secondApplicantID as string}
                    remove={cosuperannuationRemove}
                    deleteIsPending={deleteIsPending}
                  />
                  {cosuperannuationFields.length < 3 && (
                    <div className="flex items-center justify-end mt-6">
                      <AddMore
                        onClick={() =>
                          cosuperannuationAppend({
                            nameOfFund: "",
                            value: "",
                          })
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom onClick={handlePrevious}>Previous</PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartOne;
